import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Dish } from "@/api/Dishes/DishesAPI.model";
import type { DishReference } from "@/hooks/useDishReference";
import { getTranslation } from "@/helpers/getTranslation";
import { formatGrosze } from "@/helpers/cartPricing";
import type { CartSize, CartTopping } from "@/types/order";
import styles from "./DishDetails.module.scss";

interface DishDetailsProps {
  dish: Dish;
  reference: DishReference;
  language: string;
  /**
   * Sizes and toppings as read-only facts. Passed only where the dish is not
   * orderable — on the ordering screen these are controls, and listing them
   * twice would be noise.
   */
  sizes?: CartSize[];
  toppings?: CartTopping[];
  /**
   * Render only one half. The short facts belong right under the title, above
   * the description; the chip sections read better after it.
   */
  only?: "facts" | "sections";
}

/**
 * Everything the guest may want to know about a dish before ordering it:
 * serving size, diet, spice, allergens, ingredients and menu types.
 *
 * Same source data as the waiter app's dish info, laid out for a guest rather
 * than for staff. Ingredients read as a sentence instead of a wall of chips,
 * and sizes/toppings are opt-in: on the ordering screen they are interactive
 * controls, so only the browse-only menu passes them here as facts.
 */
function DishDetails(props: DishDetailsProps) {
  const {
    dish,
    reference,
    language,
    sizes = [],
    toppings = [],
    only,
  } = props;
  const { t } = useTranslation();

  const nameOf = <T extends { id: string; translations: { languageCode: string; name: string }[] }>(
    list: T[],
    id: string | undefined,
  ) => {
    if (!id) return "";
    return getTranslation(list.find((item) => item.id === id)?.translations, language);
  };

  /** Direct ingredients plus any that only apply to a particular size. */
  const ingredients = useMemo(() => {
    const ids = new Set([
      ...(dish.ingredients ?? []).map((item) => item.id),
      ...(dish.ingredientsByDishSizeId ?? []).flatMap((group) =>
        (group.ingredients ?? []).map((item) => item.id),
      ),
    ]);
    return reference.ingredients.filter((item) => ids.has(item.id));
  }, [dish, reference.ingredients]);

  /**
   * Allergens declared on the dish, plus everything its ingredients carry —
   * a dish is not safe just because nobody tagged it directly.
   */
  const allergens = useMemo(() => {
    const ids = new Set(dish.allergenIds ?? []);
    ingredients.forEach((ingredient) =>
      (ingredient.allergenIds ?? []).forEach((id) => ids.add(id)),
    );
    return reference.allergens.filter((allergen) => ids.has(allergen.id));
  }, [dish.allergenIds, ingredients, reference.allergens]);

  const dietName = nameOf(reference.diets, dish.dietId);
  const unitName = nameOf(reference.units, dish.unit?.id);
  const menuTypeNames = (dish.dishMenuTypeIds ?? [])
    .map((id) => nameOf(reference.menuTypes, id))
    .filter(Boolean);

  const spiceLevel = reference.spiceLevels.find(
    (level) => level.id === dish.spiceLevelId,
  );
  const maxSpiceLevel = reference.spiceLevels.reduce(
    (max, level) => Math.max(max, level.level ?? 0),
    0,
  );

  const hasServing = Boolean(dish.unit && unitName);
  const hasSpice = Boolean(spiceLevel && maxSpiceLevel > 0);
  const hasFacts =
    typeof dish.menuNumber === "number" ||
    hasServing ||
    Boolean(dietName) ||
    hasSpice;

  const showFacts = only !== "sections";
  const showSections = only !== "facts";
  const hasSections =
    allergens.length > 0 ||
    ingredients.length > 0 ||
    menuTypeNames.length > 0 ||
    sizes.length > 0 ||
    toppings.length > 0;

  if (!(showFacts && hasFacts) && !(showSections && hasSections)) {
    return null;
  }

  const pricedSection = (
    key: string,
    label: string,
    items: { id: string; translations: { languageCode: string; name: string }[]; price: number }[],
  ) => (
    <div className={styles.section} key={key}>
      <p className={`body-bold-medium ${styles.sectionLabel}`}>{label}</p>
      <div className={styles.badgeRow}>
        {items.map((item) => (
          <div key={item.id} className={styles.badge}>
            <p className={`body-bold-medium ${styles.badgeLabel}`}>
              {getTranslation(item.translations, language)}
            </p>
            <p className={`body-small ${styles.badgePrice}`}>
              {formatGrosze(item.price)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.dishDetails}>
      {showFacts && hasFacts ? (
        <div className={styles.factRow}>
          {typeof dish.menuNumber === "number" ? (
            <div className={styles.fact}>
              <p className={`body-bold-medium ${styles.factLabel}`}>
                {t("order.details.number")}
              </p>
              <p className={`body-bold-medium ${styles.factValue}`}>
                {dish.menuNumber}
              </p>
            </div>
          ) : null}

          {hasServing ? (
            <div className={styles.fact}>
              <p className={`body-bold-medium ${styles.factLabel}`}>
                {t("order.details.serving")}
              </p>
              <p className={`body-bold-medium ${styles.factValue}`}>
                {`${dish.unit?.quantity} ${unitName}`}
              </p>
            </div>
          ) : null}

          {dietName ? (
            <div className={styles.fact}>
              <p className={`body-bold-medium ${styles.factLabel}`}>
                {t("order.details.diet")}
              </p>
              <p className={`body-bold-medium ${styles.factValue}`}>{dietName}</p>
            </div>
          ) : null}

          {hasSpice ? (
            <div className={styles.fact}>
              <p className={`body-bold-medium ${styles.factLabel}`}>
                {t("order.details.spice")}
              </p>
              <div className={styles.peppers}>
                {Array.from({ length: maxSpiceLevel }).map((_, index) => (
                  <i
                    key={index}
                    className={`las la-pepper-hot icon-medium ${
                      index < (spiceLevel?.level ?? 0)
                        ? styles.pepperOn
                        : styles.pepperOff
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {showSections && allergens.length > 0 ? (
        <div className={styles.section}>
          <p className={`body-bold-medium ${styles.sectionLabel}`}>
            {t("order.details.allergens")}
          </p>
          <div className={styles.badgeRow}>
            {allergens.map((allergen) => (
              <div key={allergen.id} className={styles.badge}>
                {allergen.iconUrl ? (
                  <span
                    aria-hidden="true"
                    className={styles.badgeIcon}
                    style={{
                      maskImage: `url("${allergen.iconUrl}")`,
                      WebkitMaskImage: `url("${allergen.iconUrl}")`,
                    }}
                  />
                ) : null}
                <p className={`body-bold-medium ${styles.badgeLabel}`}>
                  {getTranslation(allergen.translations, language)}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {showSections && ingredients.length > 0 ? (
        <div className={styles.section}>
          <p className={`body-bold-medium ${styles.sectionLabel}`}>
            {t("order.details.ingredients")}
          </p>
          <p className={`body-medium ${styles.ingredientList}`}>
            {ingredients
              .map((ingredient) =>
                getTranslation(ingredient.translations, language),
              )
              .filter(Boolean)
              .join(", ")}
          </p>
        </div>
      ) : null}

      {showSections && sizes.length > 0
        ? pricedSection("sizes", t("order.details.sizes"), sizes)
        : null}

      {showSections && toppings.length > 0
        ? pricedSection("toppings", t("order.dish.extras"), toppings)
        : null}

      {showSections && menuTypeNames.length > 0 ? (
        <div className={styles.section}>
          <p className={`body-bold-medium ${styles.sectionLabel}`}>
            {t("order.details.menuType")}
          </p>
          <div className={styles.badgeRow}>
            {menuTypeNames.map((name) => (
              <div key={name} className={styles.badge}>
                <p className={`body-bold-medium ${styles.badgeLabel}`}>{name}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DishDetails;
